// src/features/landing/presentation/pages/AgentJobBoardDetailPage.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Footer } from "../components/Footer";
import { jobSeekerRequestRepository } from "../../infrastructure/jobSeekerRequestApiRepository";
import { recruitmentRequestRepository } from "../../infrastructure/recruitmentRequestApiRepository";
import { jobCategoryRepository } from "../../infrastructure/jobCategoryApiRepository";
import { jobOfferRepository } from "../../infrastructure/jobOfferApiRepository";
import { jobApplicationRepository } from "../../infrastructure/jobApplicationApiRepository";
import { useAuthContext } from "../../../auth/application/AuthContext";
import { regionLabel, strongSalesAreaLabel, workTypeLabel, employmentTypeLabel, supportItemLabel } from "../../domain/recruitmentOptions";

/* ── 상수 ── */
const EXPERIENCE_LABEL = {
    "신입": "신입", "1년미만": "1년 미만", "1~3년": "1~3년",
    "3~5년": "3~5년", "5~10년": "5~10년", "10년이상": "10년 이상",
};
const GENDER_LABEL = { M: "남성", F: "여성" };

/* ── 유틸 ── */
function extractRegion(address) {
    if (!address) return '';
    const parts = address.trim().split(/\s+/);
    const city = parts[0]
        .replace('특별시','').replace('광역시','').replace('특별자치시','')
        .replace('특별자치도','').replace('도','').trim();
    const district = parts[1] || '';
    return district ? `${city} ${district}` : city;
}

function maskName(name) {
    if (!name || name.length < 2) return name || '-';
    if (name.length === 2) return name[0] + '*';
    return name[0] + '*'.repeat(name.length - 2) + name[name.length - 1];
}

function formatDate(str) {
    if (!str) return '';
    return String(str).slice(0, 10);
}

function formatPeriodDate(str) {
    if (!str) return null;
    const [y, m] = String(str).split('-');
    if (!y) return null;
    return m ? `${y}.${m.padStart(2, '0')}` : y;
}

function calcDuration(startStr, endStr, isCurrent) {
    if (!startStr) return null;
    const [sy, sm = '01'] = startStr.split('-');
    const start = new Date(Number(sy), Number(sm) - 1, 1);
    const end = isCurrent ? new Date() : (() => {
        if (!endStr) return null;
        const [ey, em = '01'] = endStr.split('-');
        return new Date(Number(ey), Number(em) - 1, 1);
    })();
    if (!end) return null;
    const totalMonths = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    if (totalMonths < 0) return null;
    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;
    if (years === 0) return `${months}개월`;
    if (months === 0) return `${years}년`;
    return `${years}년 ${months}개월`;
}

function formatAmount(v) {
    if (v == null || v === '') return null;
    return Number(v).toLocaleString('ko-KR') + '원';
}

function calcAge(birthDateStr) {
    if (!birthDateStr) return null;
    const birth = new Date(birthDateStr);
    if (isNaN(birth)) return null;
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
}

/* ── 공통 컴포넌트 ── */
function Section({ title, children }) {
    return (
        <div className="ajbd-section">
            <div className="ajbd-section-title">{title}</div>
            {children}
        </div>
    );
}

function InfoGrid({ children }) {
    return <div className="ajbd-info-grid">{children}</div>;
}

function InfoRow({ label, value }) {
    if (value == null || value === '' || value === false) return null;
    return (
        <div className="ajbd-info-row">
            <span className="ajbd-info-label">{label}</span>
            <span className="ajbd-info-value">{value === true ? '예' : value}</span>
        </div>
    );
}

function TagRow({ items }) {
    if (!items || items.length === 0) return null;
    return (
        <div className="ajbd-tag-row">
            {items.map((c, i) => <span key={i} className="ajbd-tag">{c}</span>)}
        </div>
    );
}

function ContactBox({ type }) {
    const { isLoggedIn } = useAuthContext();
    const navigate = useNavigate();
    const [loginModal, setLoginModal] = useState(false);

    const isSeeker = type === 'seeker';
    const title  = isSeeker ? '채용제안 신청' : '채용 신청';
    const desc   = isSeeker
        ? '로그인 후 구직자에게 채용을 제안할 수 있습니다.'
        : '로그인 후 채용 신청서로 담당자에게 연결됩니다.';

    function handleClick() {
        if (!isLoggedIn) {
            setLoginModal(true);
        }
    }

    return (
        <>
            <div className={`ajbd-contact-box ${isSeeker ? 'ajbd-contact-box--offer' : 'ajbd-contact-box--apply'}`}>
                <div className="ajbd-contact-text">
                    <strong>{title}</strong>
                    <span>{desc}</span>
                </div>
                <button
                    className={`ajbd-contact-btn ${isSeeker ? 'ajbd-contact-btn--offer' : 'ajbd-contact-btn--apply'}`}
                    onClick={handleClick}
                >
                    {title}
                </button>
            </div>

            {loginModal && (
                <div className="ajbd-modal-overlay" onClick={() => setLoginModal(false)}>
                    <div className="ajbd-modal" onClick={e => e.stopPropagation()}>
                        <div className="ajbd-modal-title">로그인이 필요합니다</div>
                        <p className="ajbd-modal-desc">
                            {isSeeker ? '채용제안을 신청하려면' : '채용 신청을 하려면'}<br />
                            로그인이 필요합니다.
                        </p>
                        <div className="ajbd-modal-actions">
                            <button className="ajbd-modal-cancel" onClick={() => setLoginModal(false)}>취소</button>
                            <button className="ajbd-modal-submit" onClick={() => navigate('/login')}>로그인하기</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

const APPLICANT_TYPE_SHORT = {
    AGENCY_CEO: '대리점 대표',
    HR_MANAGER: '채용 담당자',
};

function HirePostCard({ post, selected, selectable, onClick }) {
    const typeLabel = APPLICANT_TYPE_SHORT[post.applicantType] || post.applicantType || '';
    const region = extractRegion(post.companyAddress);
    const updatedAt = post.updatedAt
        ? String(post.updatedAt).slice(0, 10).replace(/-/g, '.')
        : null;

    function handleDetailClick(e) {
        e.stopPropagation();
        window.open(`/premium/job-board/hire/${post.id}`, '_blank', 'noopener,noreferrer');
    }

    return (
        <div
            className={`ajbd-apply-profile-card${selected ? ' ajbd-apply-profile-card--selected' : ''}${selectable ? ' ajbd-apply-profile-card--selectable' : ''}`}
            onClick={selectable ? onClick : undefined}
            role={selectable ? 'radio' : undefined}
            aria-checked={selectable ? selected : undefined}
        >
            <div className="ajbd-apply-profile-top">
                <div className="ajbd-apply-profile-left">
                    {selectable && (
                        <span className={`ajbd-apply-radio${selected ? ' ajbd-apply-radio--on' : ''}`}>
                            {selected && (
                                <svg width="10" height="10" viewBox="0 0 10 10">
                                    <circle cx="5" cy="5" r="3" fill="#f16102" />
                                </svg>
                            )}
                        </span>
                    )}
                    {!selectable && selected && (
                        <span className="ajbd-apply-check">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f16102" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </span>
                    )}
                    <span className="ajbd-apply-profile-name">{post.companyName || '회사명 미입력'}</span>
                </div>
                <span className="ajbd-apply-profile-status" style={{ color: '#f16102' }}>{typeLabel}</span>
            </div>

            <div className="ajbd-apply-profile-meta">
                {region && <span>{region}</span>}
                {post.isActive === false && <><span className="ajbd-apply-profile-dot">·</span><span className="ajbd-apply-profile-inactive">비공개</span></>}
            </div>

            <div className="ajbd-apply-profile-footer">
                {updatedAt && (
                    <span className="ajbd-apply-profile-updated">최근 수정 {updatedAt}</span>
                )}
                <button className="ajbd-apply-detail-btn" onClick={handleDetailClick}>
                    상세보기
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="7" y1="17" x2="17" y2="7"/><polyline points="7 7 17 7 17 17"/>
                    </svg>
                </button>
            </div>
        </div>
    );
}

function JobOfferBox({ jobSeekerRequestId, alreadyOffered }) {
    const { accessToken } = useAuthContext();
    const navigate = useNavigate();
    const [message, setMessage] = useState('');
    const [open, setOpen] = useState(false);
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(alreadyOffered);
    const [error, setError] = useState(null);
    const [myPosts, setMyPosts] = useState(null);
    const [loadingPosts, setLoadingPosts] = useState(false);
    const [selectedPostId, setSelectedPostId] = useState(null);

    async function handleOpen() {
        setOpen(true);
        if (myPosts !== null) return;
        setLoadingPosts(true);
        try {
            const posts = await recruitmentRequestRepository.getMyPosts(accessToken);
            const list = posts || [];
            setMyPosts(list);
            if (list.length === 1) setSelectedPostId(list[0].id);
        } catch {
            setMyPosts([]);
        } finally {
            setLoadingPosts(false);
        }
    }

    async function handleSend() {
        setSending(true);
        setError(null);
        try {
            await jobOfferRepository.sendOffer(accessToken, jobSeekerRequestId, selectedPostId, message.trim() || null);
            setDone(true);
            setOpen(false);
        } catch (e) {
            setError(e.message || '채용제안 전송에 실패했습니다.');
        } finally {
            setSending(false);
        }
    }

    function handleClose() {
        if (!sending) setOpen(false);
    }

    if (done) {
        return (
            <div className="ajbd-contact-box ajbd-contact-box--offer ajbd-contact-box--done">
                <div className="ajbd-contact-text">
                    <strong>채용제안 전송 완료</strong>
                    <span>{alreadyOffered ? '이미 이 구직자에게 채용제안을 보내셨습니다.' : '구직자에게 이메일로 채용제안이 전달되었습니다. 관리자가 확인 후 연락드릴 예정입니다.'}</span>
                </div>
                <span className="ajbd-done-badge">제안됨</span>
            </div>
        );
    }

    const hasPosts = myPosts && myPosts.length > 0;
    const isMultiple = myPosts && myPosts.length > 1;
    const canSubmit = !!selectedPostId;

    return (
        <>
            <div className="ajbd-contact-box ajbd-contact-box--offer">
                <div className="ajbd-contact-text">
                    <strong>채용제안</strong>
                    <span>등록된 채용 신청서로 구직자에게 채용제안을 보냅니다.</span>
                </div>
                <button className="ajbd-contact-btn ajbd-contact-btn--offer" onClick={handleOpen}>
                    채용제안 보내기
                </button>
            </div>

            {open && (
                <div className="ajbd-modal-overlay" onClick={handleClose}>
                    <div className="ajbd-modal ajbd-modal--apply" onClick={e => e.stopPropagation()}>
                        <div className="ajbd-modal-title">채용제안 보내기</div>

                        {loadingPosts && (
                            <div className="ajbd-apply-loading">
                                <div className="ajbd-spinner" style={{ width: 22, height: 22 }} />
                                <span>채용 신청서 확인 중...</span>
                            </div>
                        )}

                        {!loadingPosts && !hasPosts && (
                            <div className="ajbd-apply-empty">
                                <div className="ajbd-apply-empty-icon">📋</div>
                                <p className="ajbd-apply-empty-title">등록된 채용 신청서가 없습니다</p>
                                <p className="ajbd-apply-empty-desc">
                                    채용제안을 보내려면 먼저 채용 신청서를 등록해 주세요.
                                </p>
                                <button
                                    className="ajbd-apply-register-btn"
                                    onClick={() => navigate('/premium/company')}
                                >
                                    채용 신청서 등록하러 가기
                                </button>
                            </div>
                        )}

                        {!loadingPosts && hasPosts && (
                            <>
                                <p className="ajbd-modal-desc">
                                    {isMultiple
                                        ? '제안할 채용 신청서를 선택하세요.'
                                        : '아래 채용 신청서로 제안이 전송됩니다.'}
                                </p>
                                <div className="ajbd-apply-profile-list" role={isMultiple ? 'radiogroup' : undefined}>
                                    {myPosts.map(post => (
                                        <HirePostCard
                                            key={post.id}
                                            post={post}
                                            selected={selectedPostId === post.id}
                                            selectable={isMultiple}
                                            onClick={() => setSelectedPostId(post.id)}
                                        />
                                    ))}
                                </div>
                                <textarea
                                    className="ajbd-modal-textarea"
                                    placeholder="추가 메시지 (선택) — 구직자에게 전달됩니다"
                                    value={message}
                                    onChange={e => setMessage(e.target.value)}
                                    rows={3}
                                    maxLength={500}
                                />
                                {error && <div className="ajbd-modal-error">{error}</div>}
                            </>
                        )}

                        <div className="ajbd-modal-actions">
                            <button className="ajbd-modal-cancel" onClick={handleClose} disabled={sending}>취소</button>
                            {!loadingPosts && hasPosts && (
                                <button
                                    className="ajbd-modal-submit"
                                    onClick={handleSend}
                                    disabled={sending || !canSubmit}
                                    title={!canSubmit ? '채용 신청서를 선택해주세요' : undefined}
                                >
                                    {sending ? '전송 중...' : '채용제안 전송'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

const SEEKER_STATUS_LABEL = { REG: '구직중', MATCHED: '매칭완료', CLOSED: '마감' };
const SEEKER_STATUS_COLOR = { REG: '#2e7d32', MATCHED: '#1565c0', CLOSED: '#999' };

function SeekerProfileCard({ post, selected, selectable, onClick }) {
    const region = extractRegion(post.address);
    const exp = post.jobExperienceLevel || '';
    const statusLabel = SEEKER_STATUS_LABEL[post.status] || post.status;
    const statusColor = SEEKER_STATUS_COLOR[post.status] || '#999';
    const updatedAt = post.updatedAt
        ? String(post.updatedAt).slice(0, 10).replace(/-/g, '.')
        : null;

    function handleDetailClick(e) {
        e.stopPropagation();
        window.open(`/premium/job-board/seeker/${post.id}`, '_blank', 'noopener,noreferrer');
    }

    return (
        <div
            className={`ajbd-apply-profile-card${selected ? ' ajbd-apply-profile-card--selected' : ''}${selectable ? ' ajbd-apply-profile-card--selectable' : ''}`}
            onClick={selectable ? onClick : undefined}
            role={selectable ? 'radio' : undefined}
            aria-checked={selectable ? selected : undefined}
        >
            <div className="ajbd-apply-profile-top">
                <div className="ajbd-apply-profile-left">
                    {selectable && (
                        <span className={`ajbd-apply-radio${selected ? ' ajbd-apply-radio--on' : ''}`}>
                            {selected && (
                                <svg width="10" height="10" viewBox="0 0 10 10">
                                    <circle cx="5" cy="5" r="3" fill="#f16102" />
                                </svg>
                            )}
                        </span>
                    )}
                    {!selectable && selected && (
                        <span className="ajbd-apply-check">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f16102" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </span>
                    )}
                    <span className="ajbd-apply-profile-name">{maskName(post.name)}</span>
                </div>
                <span className="ajbd-apply-profile-status" style={{ color: statusColor }}>{statusLabel}</span>
            </div>

            <div className="ajbd-apply-profile-meta">
                {exp && <span>{exp}</span>}
                {region && <><span className="ajbd-apply-profile-dot">·</span><span>{region}</span></>}
                {post.isActive === false && <><span className="ajbd-apply-profile-dot">·</span><span className="ajbd-apply-profile-inactive">비공개</span></>}
            </div>

            <div className="ajbd-apply-profile-footer">
                {updatedAt && (
                    <span className="ajbd-apply-profile-updated">최근 수정 {updatedAt}</span>
                )}
                <button className="ajbd-apply-detail-btn" onClick={handleDetailClick}>
                    상세보기
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="7" y1="17" x2="17" y2="7"/><polyline points="7 7 17 7 17 17"/>
                    </svg>
                </button>
            </div>
        </div>
    );
}

function JobApplyBox({ recruitmentRequestId, alreadyApplied }) {
    const { accessToken } = useAuthContext();
    const navigate = useNavigate();
    const [message, setMessage] = useState('');
    const [open, setOpen] = useState(false);
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(alreadyApplied);
    const [error, setError] = useState(null);
    const [myPosts, setMyPosts] = useState(null);
    const [loadingPosts, setLoadingPosts] = useState(false);
    const [selectedPostId, setSelectedPostId] = useState(null);

    async function handleOpen() {
        setOpen(true);
        if (myPosts !== null) return;
        setLoadingPosts(true);
        try {
            const posts = await jobSeekerRequestRepository.getMyPosts(accessToken);
            const list = posts || [];
            setMyPosts(list);
            if (list.length === 1) setSelectedPostId(list[0].id);
        } catch {
            setMyPosts([]);
        } finally {
            setLoadingPosts(false);
        }
    }

    async function handleApply() {
        setSending(true);
        setError(null);
        try {
            await jobApplicationRepository.apply(accessToken, recruitmentRequestId, selectedPostId, message.trim() || null);
            setDone(true);
            setOpen(false);
        } catch (e) {
            setError(e.message || '신청에 실패했습니다.');
        } finally {
            setSending(false);
        }
    }

    function handleClose() {
        if (!sending) setOpen(false);
    }

    if (done) {
        return (
            <div className="ajbd-contact-box ajbd-contact-box--apply ajbd-contact-box--done">
                <div className="ajbd-contact-text">
                    <strong>신청 완료</strong>
                    <span>{alreadyApplied ? '이미 이 구인 모집에 신청하셨습니다.' : '신청이 접수되었습니다. 관리자가 확인 후 연락드릴 예정입니다.'}</span>
                </div>
                <span className="ajbd-done-badge">신청됨</span>
            </div>
        );
    }

    const hasPosts = myPosts && myPosts.length > 0;
    const isMultiple = myPosts && myPosts.length > 1;
    const canSubmit = !!selectedPostId;

    return (
        <>
            <div className="ajbd-contact-box ajbd-contact-box--apply">
                <div className="ajbd-contact-text">
                    <strong>채용 신청</strong>
                    <span>등록된 구직 신청서로 담당자에게 연결됩니다.</span>
                </div>
                <button className="ajbd-contact-btn ajbd-contact-btn--apply" onClick={handleOpen}>
                    지원하기
                </button>
            </div>

            {open && (
                <div className="ajbd-modal-overlay" onClick={handleClose}>
                    <div className="ajbd-modal ajbd-modal--apply" onClick={e => e.stopPropagation()}>
                        <div className="ajbd-modal-title">채용 신청하기</div>

                        {loadingPosts && (
                            <div className="ajbd-apply-loading">
                                <div className="ajbd-spinner" style={{ width: 22, height: 22 }} />
                                <span>구직 신청서 확인 중...</span>
                            </div>
                        )}

                        {!loadingPosts && !hasPosts && (
                            <div className="ajbd-apply-empty">
                                <div className="ajbd-apply-empty-icon">📋</div>
                                <p className="ajbd-apply-empty-title">등록된 구직 신청서가 없습니다</p>
                                <p className="ajbd-apply-empty-desc">
                                    채용 신청을 위해서는 먼저 구직 신청서를 등록해 주세요.
                                </p>
                                <button
                                    className="ajbd-apply-register-btn"
                                    onClick={() => navigate('/premium/apply/planner')}
                                >
                                    구직 신청서 등록하러 가기
                                </button>
                            </div>
                        )}

                        {!loadingPosts && hasPosts && (
                            <>
                                <p className="ajbd-modal-desc">
                                    {isMultiple
                                        ? '지원할 구직 신청서를 선택하세요.'
                                        : '아래 구직 신청서로 지원됩니다.'}
                                </p>
                                <div className="ajbd-apply-profile-list" role={isMultiple ? 'radiogroup' : undefined}>
                                    {myPosts.map(post => (
                                        <SeekerProfileCard
                                            key={post.id}
                                            post={post}
                                            selected={selectedPostId === post.id}
                                            selectable={isMultiple}
                                            onClick={() => setSelectedPostId(post.id)}
                                        />
                                    ))}
                                </div>
                                <textarea
                                    className="ajbd-modal-textarea"
                                    placeholder="추가 메시지 (선택) — 담당자에게 전달됩니다"
                                    value={message}
                                    onChange={e => setMessage(e.target.value)}
                                    rows={3}
                                    maxLength={500}
                                />
                                {error && <div className="ajbd-modal-error">{error}</div>}
                            </>
                        )}

                        <div className="ajbd-modal-actions">
                            <button className="ajbd-modal-cancel" onClick={handleClose} disabled={sending}>취소</button>
                            {!loadingPosts && hasPosts && (
                                <button
                                    className="ajbd-modal-submit"
                                    onClick={handleApply}
                                    disabled={sending || !canSubmit}
                                    title={!canSubmit ? '신청서를 선택해주세요' : undefined}
                                >
                                    {sending ? '신청 중...' : '신청하기'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

/* ── 구직 상세 ── */
function SeekerDetail({ data, catMap, canOffer, showKakao, alreadyOffered, isOwner, onEdit, onDelete }) {
    const cats = (data.jobCategoryCodes || []).map(c => catMap[c] || c);
    const expLabel = EXPERIENCE_LABEL[data.jobExperienceLevel] || data.jobExperienceLevel || '';
    const age = calcAge(data.birthDate);

    const careers = (() => {
        try { return JSON.parse(data.careerHistories || '[]'); } catch { return []; }
    })();

    const title = cats.length > 0
        ? `${cats.slice(0, 2).join('·')} 경력 ${expLabel} 구직합니다`
        : `경력 ${expLabel} 보험 설계사 구직합니다`;

    return (
        <div className="ajbd-content">
            <div className="ajbd-meta-row">
                <div className="ajbd-badge ajbd-badge--seeker">구직</div>
                <span className="ajbd-meta-chip">등록일 {formatDate(data.createdAt)}</span>
            </div>
            <h1 className="ajbd-title">{title}</h1>

            {/* 기본 정보 */}
            <Section title="기본 정보">
                <InfoGrid>
                    <InfoRow label="이름" value={maskName(data.name)}/>
                    <InfoRow label="성별" value={GENDER_LABEL[data.gender] || data.gender}/>
                    {age != null && <InfoRow label="나이" value={`${age}세`}/>}
                    <InfoRow label="경력" value={expLabel}/>
                    <InfoRow label="지역" value={extractRegion(data.address)}/>
                    {data.educationLevel && <InfoRow label="학력" value={data.educationLevel}/>}
                </InfoGrid>
            </Section>

            {/* 희망 직종 */}
            {cats.length > 0 && (
                <Section title="희망 직종">
                    <TagRow items={cats}/>
                </Section>
            )}

            {/* 경력 사항 */}
            {careers.length > 0 && (
                <Section title="경력 사항">
                    <div className="ajbd-career-list">
                        {careers.map((c, i) => {
                            const isPlanner = c.type === 'PLANNER' || Array.isArray(c.salesTypes);
                            const startFmt = formatPeriodDate(c.startDate);
                            const endFmt = c.isCurrent ? (isPlanner ? '위촉 중' : '재직 중') : formatPeriodDate(c.endDate);
                            const duration = calcDuration(c.startDate, c.endDate, c.isCurrent);
                            const salesLabels = (c.salesTypes || [])
                                .map(code => (code === 'ETC' && c.salesTypesEtc ? c.salesTypesEtc : strongSalesAreaLabel(code)));
                            return (
                                <div key={i} className="ajbd-career-card">
                                    <div className="ajbd-career-header">
                                        <span className="ajbd-career-company">{c.companyName || '회사명 미입력'}</span>
                                        <div className="ajbd-career-badges">
                                            {!isPlanner && c.employmentType && (
                                                <span className="ajbd-career-badge">{c.employmentType}</span>
                                            )}
                                            {c.isCurrent && (
                                                <span
                                                    className="ajbd-career-badge ajbd-career-badge--active">{isPlanner ? '위촉 중' : '재직 중'}</span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="ajbd-career-meta">
                                        {isPlanner && salesLabels.length > 0 && (
                                            <div className="ajbd-career-meta-row">
                                                <span className="ajbd-career-meta-label">영업형태</span>
                                                <span className="ajbd-career-meta-value">{salesLabels.join(', ')}</span>
                                            </div>
                                        )}
                                        {!isPlanner && c.position && (
                                            <div className="ajbd-career-meta-row">
                                                <span className="ajbd-career-meta-label">직위</span>
                                                <span className="ajbd-career-meta-value">{c.position}</span>
                                            </div>
                                        )}
                                        {!isPlanner && c.jobRole && (
                                            <div className="ajbd-career-meta-row">
                                                <span className="ajbd-career-meta-label">담당 직무</span>
                                                <span className="ajbd-career-meta-value">{c.jobRole}</span>
                                            </div>
                                        )}
                                        {(startFmt || endFmt) && (
                                            <div className="ajbd-career-meta-row">
                                                <span className="ajbd-career-meta-label">{isPlanner ? '위촉 기간' : '근무 기간'}</span>
                                                <span className="ajbd-career-meta-value">
                                                    {startFmt || '?'} ~ {endFmt || '?'}
                                                    {duration &&
                                                        <span className="ajbd-career-duration"> · {duration}</span>}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {c.mainDuties && (
                                        <div className="ajbd-career-duties-wrap">
                                            <div className="ajbd-career-duties-label">{isPlanner ? '추가 업무' : '주요 업무'}</div>
                                            <div className="ajbd-career-duties">{c.mainDuties}</div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </Section>
            )}

            {canOffer ? <JobOfferBox jobSeekerRequestId={data.id} alreadyOffered={alreadyOffered}/> : showKakao &&
                <ContactBox type="seeker"/>}

            {isOwner && (
                <div className="ajbd-owner-actions">
                    <button className="ajbd-owner-btn ajbd-owner-btn--edit" onClick={onEdit}>수정</button>
                    <button className="ajbd-owner-btn ajbd-owner-btn--delete" onClick={onDelete}>삭제</button>
                </div>
            )}
        </div>
    );
}

/* ── 구인 상세 (대리점 대표 AGENCY_CEO) ── */
function AgencyDetail({data, catMap, detail, canApply, showKakao, alreadyApplied}) {
    const cats = (data.jobCategoryCodes || []).map(c => catMap[c] || c);

    const stats = [
        { label: '총무 인원',            value: detail.adminStaffCount,           unit: '명' },
        { label: '총 재직 설계사',        value: detail.insuranceAgentCount,       unit: '명' },
        { label: '활동 설계사(3개월)',    value: detail.insuranceAgentActiveCount, unit: '명' },
        { label: '설계사 평균 연령',      value: detail.insuranceAgentAvgAge,      unit: '세' },
    ].filter(s => s.value != null && s.value !== '');

    const perfs = [
        { label: '생보 실적',       amount: detail.lifeInsAmount,    count: detail.lifeInsCount },
        { label: '손보 실적',       amount: detail.nonLifeInsAmount, count: detail.nonLifeInsCount },
        { label: '자동차보험 실적', amount: detail.carInsAmount,     count: detail.carInsCount },
    ].filter(p => p.amount || p.count);

    return (
        <div className="ajbd-content">
            <div className="ajbd-meta-row">
                <div className="ajbd-badge ajbd-badge--hire">구인</div>
                <span className="ajbd-meta-chip">등록일 {formatDate(data.createdDate || data.createdAt)}</span>
                <span className="ajbd-meta-chip">지원 {data.applicationCount ?? 0}명</span>
            </div>
            <h1 className="ajbd-title">
                {data.companyName ? `${data.companyName} 설계사 모집` : '보험 설계사 모집 (대리점)'}
            </h1>

            {/* 회사 정보 */}
            <Section title="회사 정보">
                <InfoGrid>
                    <InfoRow label="회사명" value={data.companyName}/>
                    <InfoRow label="지역" value={extractRegion(data.companyAddress)}/>
                    {detail.branchName && <InfoRow label="지사명" value={detail.branchName}/>}
                </InfoGrid>
            </Section>

            {/* 채용 직무 */}
            {cats.length > 0 && (
                <Section title="채용 직무">
                    <TagRow items={cats}/>
                </Section>
            )}

            {/* 지사 현황 */}
            {stats.length > 0 && (
                <Section title="지사 현황">
                    <div className="ajbd-stat-grid">
                        {stats.map((s, i) => (
                            <div key={i} className="ajbd-stat-card">
                                <div className="ajbd-stat-value">
                                    {Number(s.value).toLocaleString()}
                                    <span className="ajbd-stat-unit">{s.unit}</span>
                                </div>
                                <div className="ajbd-stat-label">{s.label}</div>
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            {/* 실적 */}
            {perfs.length > 0 && (
                <Section title="실적">
                    <InfoGrid>
                        {perfs.map((p, i) => (
                            <InfoRow
                                key={i}
                                label={p.label}
                                value={[formatAmount(p.amount), p.count ? `${Number(p.count).toLocaleString('ko-KR')}건` : null]
                                    .filter(Boolean).join(' / ')}
                            />
                        ))}
                    </InfoGrid>
                </Section>
            )}

            {/* 채용 조건 */}
            <Section title="채용 조건">
                <InfoGrid>
                    {(detail.salaryMinMonthly || detail.salaryMaxMonthly) && (
                        <InfoRow
                            label="월 급여"
                            value={
                                detail.salaryMinMonthly && detail.salaryMaxMonthly
                                    ? `${formatAmount(detail.salaryMinMonthly)} ~ ${formatAmount(detail.salaryMaxMonthly)}`
                                    : formatAmount(detail.salaryMinMonthly || detail.salaryMaxMonthly)
                            }
                        />
                    )}
                    {detail.basicInsuranceStatus != null && (
                        <InfoRow label="4대 보험" value={detail.basicInsuranceStatus ? '가입' : '미가입'}/>
                    )}
                    <InfoRow label="근무 요일" value={detail.workingDays}/>
                    <InfoRow label="근무 시간" value={detail.workStartTime && detail.workEndTime ? `${detail.workStartTime} ~ ${detail.workEndTime}` : null}/>
                    <InfoRow label="입사 희망일" value={detail.preferredStartDate}/>
                </InfoGrid>
            </Section>

            {/* 복지 사항 */}
            {detail.benefits && (
                <Section title="복지 사항">
                    <div className="ajbd-text-block">{detail.benefits}</div>
                </Section>
            )}

            {/* 지사 강점 */}
            {detail.branchStrengths && (
                <Section title="지사 강점 및 특이사항">
                    <div className="ajbd-text-block">{detail.branchStrengths}</div>
                </Section>
            )}

            {canApply ? <JobApplyBox recruitmentRequestId={data.id} alreadyApplied={alreadyApplied}/> : showKakao &&
                <ContactBox type="hire"/>}
        </div>
    );
}

/* ── 구인 상세 (채용 담당자 HR_MANAGER) ── */
function ManagerDetail({data, catMap, detail, canApply, showKakao, alreadyApplied}) {
    const cats = (data.jobCategoryCodes || []).map(c => catMap[c] || c);

    return (
        <div className="ajbd-content">
            <div className="ajbd-meta-row">
                <div className="ajbd-badge ajbd-badge--hire">구인</div>
                <span className="ajbd-meta-chip">등록일 {formatDate(data.createdDate || data.createdAt)}</span>
                <span className="ajbd-meta-chip">지원 {data.applicationCount ?? 0}명</span>
            </div>
            <h1 className="ajbd-title">
                {data.companyName ? `${data.companyName} 설계사 모집` : '보험 설계사 모집 (보험사)'}
            </h1>

            {/* 회사 정보 */}
            <Section title="회사 정보">
                <InfoGrid>
                    <InfoRow label="회사명" value={data.companyName}/>
                    <InfoRow label="지역" value={extractRegion(data.companyAddress)}/>
                </InfoGrid>
            </Section>

            {/* 채용 직무 */}
            {cats.length > 0 && (
                <Section title="채용 직무">
                    <TagRow items={cats}/>
                </Section>
            )}

            {/* 채용 요건 */}
            {detail.recruitmentRequirements && (
                <Section title="채용 요건">
                    <div className="ajbd-text-block">{detail.recruitmentRequirements}</div>
                </Section>
            )}

            {/* 채용 조건 */}
            <Section title="채용 조건">
                <InfoGrid>
                    {(detail.salaryMinMonthly || detail.salaryMaxMonthly) && (
                        <InfoRow
                            label="월 급여"
                            value={
                                detail.salaryMinMonthly && detail.salaryMaxMonthly
                                    ? `${formatAmount(detail.salaryMinMonthly)} ~ ${formatAmount(detail.salaryMaxMonthly)}`
                                    : formatAmount(detail.salaryMinMonthly || detail.salaryMaxMonthly)
                            }
                        />
                    )}
                    {detail.basicInsuranceStatus != null && (
                        <InfoRow label="4대 보험" value={detail.basicInsuranceStatus ? '가입' : '미가입'}/>
                    )}
                    <InfoRow label="근무 요일" value={detail.workingDays}/>
                    <InfoRow label="근무 시간" value={detail.workStartTime && detail.workEndTime ? `${detail.workStartTime} ~ ${detail.workEndTime}` : null}/>
                    <InfoRow label="입사 희망일" value={detail.preferredStartDate}/>
                </InfoGrid>
            </Section>

            {/* 복지 사항 */}
            {detail.benefits && (
                <Section title="복지 사항">
                    <div className="ajbd-text-block">{detail.benefits}</div>
                </Section>
            )}

            {canApply ? <JobApplyBox recruitmentRequestId={data.id} alreadyApplied={alreadyApplied}/> : showKakao &&
                <ContactBox type="hire"/>}
        </div>
    );
}

/* ── 구인 상세 (설계사 채용관 · 보험설계사 채용 AGENCY_PLANNER) ── */
function PlannerDetail({data, detail, canApply, showKakao, alreadyApplied}) {
    const regions = (data.recruitRegions || []).map(regionLabel);

    const salesAreas = (detail.strongSalesAreas || [])
        .map(c => (c === 'ETC' && detail.strongSalesAreasEtc ? detail.strongSalesAreasEtc : strongSalesAreaLabel(c)));

    const supports = (detail.supportItems || [])
        .map(c => (c === 'ETC' && detail.supportItemsEtc ? detail.supportItemsEtc : supportItemLabel(c)));

    // 지사 현황 (보험대리점 대표 AGENCY_CEO 진입 건에만 채워짐)
    const branchStats = [
        { label: '총무 인원',            value: detail.adminStaffCount,           unit: '명' },
        { label: '총 재직 설계사',        value: detail.insuranceAgentCount,       unit: '명' },
        { label: '활동 설계사(3개월)',    value: detail.insuranceAgentActiveCount, unit: '명' },
        { label: '설계사 평균 연령',      value: detail.insuranceAgentAvgAge,      unit: '세' },
    ].filter(s => s.value != null && s.value !== '');

    // 이직 시 제공 조건 — 라벨이 길고 값도 자유 텍스트라 InfoRow(고정폭 라벨) 대신
    // 라벨-위/값-아래 카드 그리드로 표시 (2026-09-13 UI 개선)
    const relocationConditions = [
        { label: '정착지원금 또는 교육비', value: detail.relocationSettlementSupport },
        { label: '근무 형태',            value: detail.relocationWorkType ? workTypeLabel(detail.relocationWorkType) : null },
        { label: '고용 형태',            value: (detail.employmentTypes && detail.employmentTypes.length > 0) ? detail.employmentTypes.map(employmentTypeLabel).join(', ') : null },
        { label: '영업 베네핏',          value: detail.salesBenefit },
        { label: '교육 시스템',          value: detail.educationSystem },
        { label: '평균 소득',            value: detail.averageIncome },
        { label: '기본수수료 + 프로모션', value: detail.baseCommissionPromotion },
    ].filter(c => c.value != null && c.value !== '');

    return (
        <div className="ajbd-content">
            <div className="ajbd-meta-row">
                <div className="ajbd-badge ajbd-badge--hire">구인</div>
                <span className="ajbd-meta-chip">등록일 {formatDate(data.createdDate || data.createdAt)}</span>
                <span className="ajbd-meta-chip">지원 {data.applicationCount ?? 0}명</span>
            </div>
            <h1 className="ajbd-title">
                {data.companyName ? `${data.companyName} 보험 설계사 모집` : '보험 설계사 모집'}
            </h1>

            {/* 회사 정보 */}
            <Section title="회사 정보">
                <InfoGrid>
                    <InfoRow label="회사명" value={data.companyName}/>
                    <InfoRow label="지역" value={extractRegion(data.companyAddress)}/>
                    <InfoRow label="등록 유형" value={APPLICANT_TYPE_SHORT[data.applicantType]}/>
                    {detail.branchName && <InfoRow label="지사명" value={detail.branchName}/>}
                </InfoGrid>
            </Section>

            {/* 지사 현황 */}
            {branchStats.length > 0 && (
                <Section title="지사 현황">
                    <div className="ajbd-stat-grid">
                        {branchStats.map((s, i) => (
                            <div key={i} className="ajbd-stat-card">
                                <div className="ajbd-stat-value">
                                    {Number(s.value).toLocaleString()}
                                    <span className="ajbd-stat-unit">{s.unit}</span>
                                </div>
                                <div className="ajbd-stat-label">{s.label}</div>
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            {/* 채용 지역 */}
            {regions.length > 0 && (
                <Section title="채용 지역">
                    <TagRow items={regions}/>
                </Section>
            )}

            {/* 강점 영업 영역 */}
            {salesAreas.length > 0 && (
                <Section title="강점 영업 영역">
                    <TagRow items={salesAreas}/>
                </Section>
            )}

            {/* 이직 시 제공 조건 */}
            {relocationConditions.length > 0 && (
                <Section title="이직 시 제공 조건">
                    <div className="ajbd-condition-grid">
                        {relocationConditions.map((c, i) => (
                            <div key={i} className="ajbd-condition-card">
                                <div className="ajbd-condition-label">{c.label}</div>
                                <div className="ajbd-condition-value">{c.value}</div>
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            {/* 지원 사항 */}
            {supports.length > 0 && (
                <Section title="지원 사항">
                    <TagRow items={supports}/>
                </Section>
            )}

            {/* 채용요청사항 */}
            {detail.recruitmentRequirements && (
                <Section title="채용요청사항">
                    <div className="ajbd-text-block">{detail.recruitmentRequirements}</div>
                </Section>
            )}

            {/* 복지사항 */}
            {detail.benefits && (
                <Section title="복지사항">
                    <div className="ajbd-text-block">{detail.benefits}</div>
                </Section>
            )}

            {/* 지원 사항 상세 (레거시) — 2026-09-13 채용요청사항/복지사항으로 분리되기 전에
                등록된 게시글만 이 필드에 값이 남아있어 표시 */}
            {!detail.recruitmentRequirements && !detail.benefits && detail.supportDetail && (
                <Section title="지원 사항 상세">
                    <div className="ajbd-text-block">{detail.supportDetail}</div>
                </Section>
            )}

            {/* 지사 강점 */}
            {detail.branchStrengths && (
                <Section title="지사 강점 및 특이사항">
                    <div className="ajbd-text-block">{detail.branchStrengths}</div>
                </Section>
            )}

            {canApply ? <JobApplyBox recruitmentRequestId={data.id} alreadyApplied={alreadyApplied}/> : showKakao &&
                <ContactBox type="hire"/>}
        </div>
    );
}

/* ── 메인 페이지 ── */
export function AgentJobBoardDetailPage() {
    const {type, id} = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { member, accessToken } = useAuthContext();
    const [data, setData] = useState(null);
    const [catMap, setCatMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [alreadyApplied, setAlreadyApplied] = useState(false);
    const [alreadyOffered, setAlreadyOffered] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const isOwner = type === 'seeker' && location.state?.isOwner === true;

    const isJobSeeker = member?.role === 'JOB_SEEKER';
    const isCompany   = member?.role === 'COMPANY';

    const canOffer = type === 'seeker'
        && isCompany
        && member?.companyStatus === 'APPROVED';

    const canApply = type === 'hire' && isJobSeeker;

    // 구직 상세: 개인회원(JOB_SEEKER)에게는 카카오 버튼 미표시
    // 구인 상세: 기업회원(COMPANY)에게는 카카오 버튼 미표시
    const showKakao = type === 'seeker' ? !isJobSeeker : !isCompany;

    useEffect(() => {
        async function load() {
            setLoading(true);
            setError(null);
            try {
                const requests = [
                    jobCategoryRepository.getJobCategories(),
                    type === 'seeker'
                        ? jobSeekerRequestRepository.getById(id)
                        : recruitmentRequestRepository.getById(id),
                ];
                if (canApply && accessToken) {
                    requests.push(jobApplicationRepository.checkApplied(accessToken, id));
                } else if (canOffer && accessToken) {
                    requests.push(jobOfferRepository.checkOffered(accessToken, id));
                }

                const [catData, detail, checkResult] = await Promise.all(requests);
                const map = {};
                catData.forEach(c => { map[c.code] = c.name; });
                setCatMap(map);
                setData(detail);
                if (checkResult?.applied) setAlreadyApplied(true);
                if (checkResult?.offered) setAlreadyOffered(true);
            } catch (e) {
                setError(e.message || '데이터를 불러오는 중 오류가 발생했습니다.');
            } finally {
                setLoading(false);
            }
        }
        load();
    }, [type, id, canApply, canOffer, accessToken]);

    function handleEdit() {
        // 설계사 채용관 구직글(희망직종 INSURANCE_PLANNER)이면 설계사 전용 수정 폼으로.
        const isPlanner = (data.jobCategoryCodes || []).includes('INSURANCE_PLANNER');
        navigate(isPlanner ? '/premium/apply/planner' : '/premium/apply', {
            state: { editMode: true, postId: Number(id), initialData: data },
        });
    }

    async function handleDelete() {
        setDeleting(true);
        try {
            await jobSeekerRequestRepository.deleteById(id, accessToken);
            navigate('/account?tab=my-seeker-posts', { replace: true });
        } catch (err) {
            alert(err.message || '삭제에 실패했습니다.');
        } finally {
            setDeleting(false);
            setDeleteConfirm(false);
        }
    }

    function renderDetail() {
        if (type === 'seeker') return <SeekerDetail data={data} catMap={catMap} canOffer={canOffer} showKakao={showKakao} alreadyOffered={alreadyOffered} isOwner={isOwner} onEdit={handleEdit} onDelete={() => setDeleteConfirm(true)} />;

        const detail = (() => {
            if (!data.detailData) return {};
            if (typeof data.detailData === 'object') return data.detailData;
            try { return JSON.parse(data.detailData); } catch { return {}; }
        })();

        if (data.targetType === 'PLANNER') return <PlannerDetail data={data} detail={detail} canApply={canApply} showKakao={showKakao} alreadyApplied={alreadyApplied} />;
        if (data.applicantType === 'AGENCY_CEO') return <AgencyDetail data={data} catMap={catMap} detail={detail} canApply={canApply} showKakao={showKakao} alreadyApplied={alreadyApplied} />;
        return <ManagerDetail data={data} catMap={catMap} detail={detail} canApply={canApply} showKakao={showKakao} alreadyApplied={alreadyApplied} />;
    }

    return (
        <>
            <div className="ajbd-page">
                <div className="ajbd-body">
                    <button className="ajbd-back" onClick={() => navigate('/premium/job-board')}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 18 9 12 15 6"/>
                        </svg>
                        목록으로
                    </button>

                    {loading && (
                        <div className="ajbd-state">
                            <div className="ajbd-spinner" />
                            <span>불러오는 중...</span>
                        </div>
                    )}

                    {!loading && error && (
                        <div className="ajbd-state ajbd-state--error">{error}</div>
                    )}

                    {!loading && !error && data && renderDetail()}
                </div>
            </div>

            {deleteConfirm && (
                <div className="ajbd-modal-overlay" onClick={() => !deleting && setDeleteConfirm(false)}>
                    <div className="ajbd-modal" onClick={e => e.stopPropagation()}>
                        <div className="ajbd-modal-title">구직글 삭제</div>
                        <p className="ajbd-modal-desc">삭제하면 복구할 수 없습니다. 정말 삭제하시겠습니까?</p>
                        <div className="ajbd-modal-actions">
                            <button className="ajbd-modal-cancel" onClick={() => setDeleteConfirm(false)} disabled={deleting}>취소</button>
                            <button className="ajbd-modal-submit ajbd-modal-submit--danger" onClick={handleDelete} disabled={deleting}>
                                {deleting ? '삭제 중...' : '삭제'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Footer />

            <style>{`
                .ajbd-page { padding-top: 80px; min-height: 100vh; background: #f7f4f1; }
                .ajbd-body { max-width: 760px; margin: 0 auto; padding: 36px 20px 80px; }

                .ajbd-back {
                    display: inline-flex; align-items: center; gap: 6px;
                    background: none; border: none; cursor: pointer;
                    font-size: 0.88rem; font-weight: 600; color: #888;
                    padding: 0; margin-bottom: 20px; transition: color 0.15s;
                }
                .ajbd-back:hover { color: #f16102; }

                .ajbd-state {
                    display: flex; flex-direction: column; align-items: center;
                    gap: 12px; padding: 80px 20px; color: #aaa; font-size: 0.9rem;
                }
                .ajbd-state--error { color: #e53935; }
                .ajbd-spinner {
                    width: 28px; height: 28px; border: 3px solid #fde0c8;
                    border-top-color: #f16102; border-radius: 50%;
                    animation: ajbd-spin 0.7s linear infinite;
                }
                @keyframes ajbd-spin { to { transform: rotate(360deg); } }

                /* 카드 */
                .ajbd-content {
                    background: #fff; border-radius: 16px;
                    padding: 32px 28px; box-shadow: 0 4px 20px rgba(0,0,0,0.07);
                    display: flex; flex-direction: column; gap: 0;
                }

                .ajbd-badge {
                    display: inline-flex; align-items: center; padding: 4px 12px;
                    border-radius: 20px; font-size: 0.8rem; font-weight: 700;
                    width: fit-content;
                }
                .ajbd-badge--seeker { background: #e8f4fd; color: #1976d2; }
                .ajbd-badge--hire   { background: #fff3e0; color: #f16102; }

                .ajbd-title {
                    font-size: 1.35rem; font-weight: 800; color: #1c1814;
                    line-height: 1.4; margin: 0 0 14px;
                }

                .ajbd-meta-row { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 28px; }
                .ajbd-meta-chip {
                    display: inline-flex; align-items: center; gap: 4px;
                    padding: 5px 12px; background: #f7f4f1; border-radius: 20px;
                    font-size: 0.82rem; color: #666; font-weight: 500;
                }

                /* 섹션 */
                .ajbd-section {
                    padding: 22px 0;
                    border-top: 1px solid #f0ebe6;
                }
                .ajbd-section:last-of-type { border-bottom: 1px solid #f0ebe6; }

                .ajbd-section-title {
                    font-size: 0.85rem; font-weight: 700; letter-spacing: 0.08em;
                    text-align: left;
                    text-transform: uppercase; color: #000000;
                    margin-bottom: 14px;
                }

                /* 정보 그리드 */
                .ajbd-info-grid { display: flex; flex-direction: column; gap: 11px; }
                .ajbd-info-row { display: flex; gap: 12px; font-size: 0.9rem; align-items: flex-start; }
                .ajbd-info-label {
                    flex-shrink: 0; width: 96px;
                    color: #747474; font-weight: 600; font-size: 0.82rem; padding-top: 1px;
                }
                .ajbd-info-value { color: #2c2c2c; font-weight: 500; line-height: 1.5; }

                /* 태그 */
                .ajbd-tag-row { display: flex; flex-wrap: wrap; gap: 8px; }
                .ajbd-tag {
                    padding: 6px 14px; background: #fff8f4;
                    border: 1.5px solid #fde0c8; border-radius: 8px;
                    font-size: 0.85rem; font-weight: 600; color: #f16102;
                }

                /* 경력 카드 */
                .ajbd-career-list { display: flex; flex-direction: column; gap: 14px; }
                .ajbd-career-card {
                    border: 1.5px solid #e5ddd7; border-radius: 12px; padding: 18px 18px 16px;
                    display: flex; flex-direction: column; gap: 12px;
                }
                .ajbd-career-header {
                    display: flex; align-items: center; justify-content: space-between; gap: 10px;
                }
                .ajbd-career-company { font-size: 1rem; font-weight: 700; color: #1c1814; }
                .ajbd-career-badges { display: flex; gap: 6px; flex-shrink: 0; }
                .ajbd-career-badge {
                    font-size: 0.73rem; background: #f0ebe6; color: #7a6a5e;
                    padding: 3px 9px; border-radius: 10px; font-weight: 600;
                }
                .ajbd-career-badge--active {
                    background: #e8f5e9; color: #2e7d32;
                }

                .ajbd-career-meta { display: flex; flex-direction: column; gap: 7px; }
                .ajbd-career-meta-row { display: flex; gap: 10px; align-items: baseline; font-size: 0.88rem; }
                .ajbd-career-meta-label {
                    flex-shrink: 0; width: 72px;
                    font-size: 0.78rem; font-weight: 700; color: #aaa;
                }
                .ajbd-career-meta-value { color: #2c2c2c; font-weight: 500; line-height: 1.5; }
                .ajbd-career-duration { color: #f16102; font-weight: 700; }

                .ajbd-career-duties-wrap {
                    border-top: 1px solid #f0ebe6; padding-top: 12px;
                    display: flex; flex-direction: column; gap: 6px;
                }
                .ajbd-career-duties-label {
                    font-size: 0.78rem; font-weight: 700; color: #aaa;
                }
                .ajbd-career-duties {
                    font-size: 0.88rem; color: #555; line-height: 1.75;
                    white-space: pre-line;
                }

                /* 통계 */
                .ajbd-stat-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
                    gap: 12px;
                }
                .ajbd-stat-card {
                    background: #faf7f4; border-radius: 10px; padding: 18px 14px;
                    text-align: center;
                }
                .ajbd-stat-value {
                    font-size: 1.5rem; font-weight: 800; color: #f16102; line-height: 1.2;
                }
                .ajbd-stat-unit { font-size: 0.85rem; font-weight: 600; margin-left: 2px; }
                .ajbd-stat-label { font-size: 0.78rem; color: #888; margin-top: 5px; }

                /* 이직 시 제공 조건 — 라벨 위/값 아래 카드형 (라벨 길이가 들쭉날쭉해 InfoRow 고정폭 대신 사용) */
                .ajbd-condition-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
                    gap: 10px;
                }
                .ajbd-condition-card {
                    background: #faf7f4; border-radius: 10px; padding: 14px 16px;
                }
                .ajbd-condition-label {
                    font-size: 0.76rem; font-weight: 700; color: #a89890;
                    letter-spacing: 0.02em; margin-bottom: 6px;
                }
                .ajbd-condition-value {
                    font-size: 0.92rem; font-weight: 600; color: #2c2c2c;
                    line-height: 1.5; word-break: break-word;
                }

                /* 텍스트 블록 */
                .ajbd-text-block {
                    font-size: 0.9rem; color: #444; line-height: 1.8;
                    white-space: pre-line; background: #faf7f4;
                    border-radius: 8px; padding: 14px 16px;
                }

                /* 상담 박스 */
                .ajbd-contact-box {
                    display: flex; align-items: center; gap: 14px;
                    background: #fff8f4; border: 1.5px solid #fde0c8;
                    border-radius: 12px; padding: 18px 20px; margin-top: 24px;
                }
                .ajbd-contact-icon { font-size: 1.6rem; flex-shrink: 0; }
                .ajbd-contact-text { display: flex; flex-direction: column; gap: 3px; flex: 1; }
                .ajbd-contact-text strong { font-size: 0.95rem; font-weight: 700; color: #1c1814; }
                .ajbd-contact-text span { font-size: 0.82rem; color: #888; }
                .ajbd-contact-btn {
                    flex-shrink: 0; padding: 10px 18px; background: #f16102;
                    color: #fff; border-radius: 8px; text-decoration: none;
                    font-size: 0.85rem; font-weight: 700; transition: background 0.2s;
                    white-space: nowrap;
                }
                .ajbd-contact-btn:hover { background: #d95502; }
                .ajbd-contact-box--offer,
                .ajbd-contact-box--apply {
                    border: 2px solid #f16102;
                    background: #fff;
                }
                .ajbd-contact-box--done {
                    border-color: #bbb;
                    background: #fafafa;
                }
                .ajbd-done-badge {
                    flex-shrink: 0; padding: 6px 14px;
                    background: #f0f0f0; color: #888;
                    border-radius: 8px; font-size: 0.82rem; font-weight: 700;
                    white-space: nowrap;
                }
                .ajbd-contact-btn--offer,
                .ajbd-contact-btn--apply {
                    background: #f16102;
                    padding: 12px 24px;
                    border-radius: 10px;
                    font-size: 0.9rem;
                    letter-spacing: 0.02em;
                    box-shadow: 0 2px 8px rgba(241, 97, 2, 0.28);
                }
                .ajbd-contact-btn--offer:hover,
                .ajbd-contact-btn--apply:hover {
                    background: #d95502;
                    box-shadow: 0 4px 14px rgba(241, 97, 2, 0.38);
                }

                /* 모달 */
                .ajbd-modal-overlay {
                    position: fixed; inset: 0; background: rgba(0,0,0,0.45);
                    display: flex; align-items: center; justify-content: center;
                    z-index: 1000; padding: 20px;
                }
                .ajbd-modal {
                    background: #fff; border-radius: 16px; padding: 28px 24px;
                    width: 100%; max-width: 480px; box-shadow: 0 8px 40px rgba(0,0,0,0.15);
                }
                .ajbd-modal-title { font-size: 1.1rem; font-weight: 800; color: #1c1814; margin-bottom: 8px; }
                .ajbd-modal-desc { font-size: 0.85rem; color: #888; margin-bottom: 16px; line-height: 1.5; }
                .ajbd-modal-textarea {
                    width: 100%; border: 1.5px solid #e0e0e0; border-radius: 8px;
                    padding: 12px; font-size: 0.9rem; resize: vertical;
                    font-family: inherit; box-sizing: border-box;
                    transition: border-color 0.15s;
                }
                .ajbd-modal-textarea:focus { outline: none; border-color: #f16102; }
                .ajbd-modal-error { color: #e53935; font-size: 0.82rem; margin-top: 8px; }
                .ajbd-modal-actions {
                    display: flex; justify-content: flex-end; gap: 10px; margin-top: 20px;
                }
                .ajbd-modal-cancel {
                    padding: 10px 20px; border-radius: 8px;
                    border: 1.5px solid #e0e0e0; background: #fff;
                    font-size: 0.88rem; font-weight: 600; color: #666; cursor: pointer;
                }
                .ajbd-modal-cancel:hover { background: #f5f5f5; }
                .ajbd-modal-submit {
                    padding: 10px 20px; border-radius: 8px; border: none;
                    background: #f16102; color: #fff;
                    font-size: 0.88rem; font-weight: 700; cursor: pointer;
                    transition: background 0.2s;
                }
                .ajbd-modal-submit:hover:not(:disabled) { background: #d95502; }
                .ajbd-modal-submit:disabled { background: #fcc594; cursor: not-allowed; }
                .ajbd-modal-submit--danger { background: #e53935; }

                /* 지원하기 팝업 — 모달 너비 */
                .ajbd-modal--apply { max-width: 520px; }

                /* 지원하기 — 로딩 */
                .ajbd-apply-loading {
                    display: flex; align-items: center; gap: 10px; justify-content: center;
                    padding: 28px 0; color: #aaa; font-size: 0.88rem;
                }

                /* 지원하기 — 신청서 카드 목록 */
                .ajbd-apply-profile-list {
                    display: flex; flex-direction: column; gap: 8px;
                    margin-bottom: 14px;
                    max-height: 260px; overflow-y: auto;
                    padding-right: 2px;
                }
                .ajbd-apply-profile-list::-webkit-scrollbar { width: 4px; }
                .ajbd-apply-profile-list::-webkit-scrollbar-thumb { background: #e0d8d2; border-radius: 4px; }

                .ajbd-apply-profile-card {
                    border: 1.5px solid #e5ddd7; border-radius: 10px; padding: 12px 14px;
                    display: flex; flex-direction: column; gap: 6px;
                    background: #faf7f4;
                    transition: border-color 0.15s, background 0.15s;
                }
                .ajbd-apply-profile-card--selectable {
                    cursor: pointer;
                }
                .ajbd-apply-profile-card--selectable:hover {
                    border-color: #fcc594; background: #fff8f4;
                }
                .ajbd-apply-profile-card--selected {
                    border-color: #f16102; background: #fff8f4;
                    box-shadow: 0 0 0 1px #f16102;
                }

                .ajbd-apply-profile-top {
                    display: flex; align-items: center; justify-content: space-between; gap: 8px;
                }
                .ajbd-apply-profile-left {
                    display: flex; align-items: center; gap: 8px;
                }
                .ajbd-apply-radio {
                    width: 18px; height: 18px; border-radius: 50%;
                    border: 2px solid #d0c8c2; flex-shrink: 0;
                    display: flex; align-items: center; justify-content: center;
                    transition: border-color 0.15s;
                }
                .ajbd-apply-radio--on { border-color: #f16102; }
                .ajbd-apply-check { display: flex; align-items: center; flex-shrink: 0; }
                .ajbd-apply-profile-name { font-size: 0.95rem; font-weight: 700; color: #1c1814; }
                .ajbd-apply-profile-status { font-size: 0.78rem; font-weight: 700; flex-shrink: 0; }
                .ajbd-apply-profile-meta {
                    display: flex; align-items: center; gap: 5px;
                    font-size: 0.82rem; color: #888; padding-left: 2px;
                }
                .ajbd-apply-profile-dot { color: #ddd; }
                .ajbd-apply-profile-inactive { color: #bbb; }

                .ajbd-apply-profile-footer {
                    display: flex; align-items: center; justify-content: space-between;
                    padding-top: 8px; margin-top: 2px;
                    border-top: 1px solid #ede8e3;
                }
                .ajbd-apply-profile-updated {
                    font-size: 0.75rem; color: #bbb;
                }
                .ajbd-apply-detail-btn {
                    display: inline-flex; align-items: center; gap: 3px;
                    padding: 3px 8px; border-radius: 5px;
                    border: 1px solid #e0d8d2; background: #fff;
                    font-size: 0.75rem; font-weight: 600; color: #888;
                    cursor: pointer; transition: border-color 0.15s, color 0.15s;
                    white-space: nowrap;
                }
                .ajbd-apply-detail-btn:hover { border-color: #f16102; color: #f16102; }

                /* 지원하기 — 구직 신청서 없음 */
                .ajbd-apply-empty {
                    display: flex; flex-direction: column; align-items: center;
                    gap: 8px; padding: 20px 0 10px; text-align: center;
                }
                .ajbd-apply-empty-icon { font-size: 2.2rem; }
                .ajbd-apply-empty-title { font-size: 0.95rem; font-weight: 700; color: #1c1814; margin: 0; }
                .ajbd-apply-empty-desc { font-size: 0.83rem; color: #888; line-height: 1.6; margin: 0; }
                .ajbd-apply-register-btn {
                    margin-top: 6px; padding: 10px 22px; border-radius: 8px; border: none;
                    background: #f16102; color: #fff;
                    font-size: 0.88rem; font-weight: 700; cursor: pointer;
                    transition: background 0.2s;
                }
                .ajbd-apply-register-btn:hover { background: #d95502; }
                .ajbd-modal-submit--danger:hover:not(:disabled) { background: #c62828; }
                .ajbd-modal-submit--danger:disabled { background: #ef9a9a; }

                /* 소유자 수정/삭제 버튼 */
                .ajbd-owner-actions {
                    display: flex; gap: 10px; justify-content: flex-end;
                    padding-top: 20px; margin-top: 4px;
                    border-top: 1px solid #f0ebe6;
                }
                .ajbd-owner-btn {
                    padding: 9px 22px; border-radius: 8px; font-size: 0.88rem; font-weight: 700;
                    cursor: pointer; border: 1.5px solid; transition: background 0.15s, color 0.15s;
                    white-space: nowrap;
                }
                .ajbd-owner-btn--edit {
                    border-color: #f16102; color: #f16102; background: #fff;
                }
                .ajbd-owner-btn--edit:hover { background: #f16102; color: #fff; }
                .ajbd-owner-btn--delete {
                    border-color: #e53935; color: #e53935; background: #fff;
                }
                .ajbd-owner-btn--delete:hover { background: #e53935; color: #fff; }

                @media (max-width: 640px) {
                    .ajbd-body { padding: 24px 14px 56px; }
                    .ajbd-content { padding: 22px 16px; }
                    .ajbd-title { font-size: 1.15rem; }
                    .ajbd-stat-grid { grid-template-columns: repeat(2, 1fr); }
                    .ajbd-condition-grid { grid-template-columns: 1fr; }
                    .ajbd-contact-box { flex-direction: column; text-align: center; }
                    .ajbd-contact-btn { width: 100%; text-align: center; }
                    .ajbd-info-label { width: 76px; }
                }
            `}</style>
        </>
    );
}
